import { Controller } from "@hotwired/stimulus"

/**
 * Priority+ navigation pattern: shows as many items as fit,
 * puts the rest in a "More" dropdown. Measures actual rendered
 * widths and recalculates on resize via ResizeObserver.
 */
export default class extends Controller {
  static targets = ["container", "item", "more", "overflowItem"]

  connect() {
    this.observer = new ResizeObserver(() => {
      cancelAnimationFrame(this.frameId)
      this.frameId = requestAnimationFrame(() => this.fit())
    })
    this.observer.observe(this.element)
    this.frameId = requestAnimationFrame(() => this.fit())
  }

  disconnect() {
    this.observer?.disconnect()
    cancelAnimationFrame(this.frameId)
  }

  fit() {
    const items = this.itemTargets
    const container = this.containerTarget
    const more = this.moreTarget
    const gap = parseFloat(getComputedStyle(container).columnGap) || 0

    // Reset: show all pills, hide More
    items.forEach(el => { el.style.display = "" })
    more.style.display = "none"

    const widths = items.map(el => el.offsetWidth)
    const totalWidth = widths.reduce((sum, w, i) => sum + w + (i > 0 ? gap : 0), 0)

    // All items fit without More button — done
    if (totalWidth <= container.clientWidth) {
      this.syncOverflow(items.length)
      return
    }

    // Show More button, measure available space for pills
    more.style.display = ""
    const available = container.clientWidth
    let used = 0
    let cutoff = 0

    for (let i = 0; i < items.length; i++) {
      const next = widths[i] + (i > 0 ? gap : 0)
      if (used + next > available) break
      used += next
      cutoff = i + 1
    }

    cutoff = Math.max(cutoff, 1)
    items.forEach((el, i) => { el.style.display = i >= cutoff ? "none" : "" })
    this.syncOverflow(cutoff)
  }

  syncOverflow(cutoff) {
    const allVisible = cutoff >= this.itemTargets.length

    this.overflowItemTargets.forEach(el => {
      const index = parseInt(el.dataset.index, 10)
      el.style.display = (!allVisible && index >= cutoff) ? "" : "none"
    })

    const btn = this.moreTarget.querySelector("button")
    if (!btn) return

    const hasActive = !allVisible && this.overflowItemTargets.some(el =>
      parseInt(el.dataset.index, 10) >= cutoff &&
      el.getAttribute("aria-current") === "page"
    )
    // bg-cyan-100 text-cyan-700 — referenced here so Tailwind generates them
    btn.classList.toggle("bg-cyan-100", hasActive)
    btn.classList.toggle("text-cyan-700", hasActive)
  }
}
